/**
 * The small pieces the Materials tab, the Swap sheet and the Order sheet draw
 * more than once — Boards 2, 3, 4, 16 C and 18 B, C, their numbers taken from
 * the boards' HTML.
 *
 * Words come from `copy.ts`; colours from the tokens (the eight chips of §8.2
 * are `semantic.material`, drawn by the app's `StatusChip`). Nothing here
 * adds, rounds or compares a number: the server sent every figure.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type {
  DayRef, Delta, MaterialGroupKey, MaterialLine, MaterialSource, MaterialState, PoRef, Unit,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatNumber } from '../lib/format.js'
import { StatusChip } from '../components/primitives.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { COMMON, MATERIALS } from './copy.js'
import { money, signedMoney } from './format.js'

// ---------------------------------------------------------------------------
// Chips, tags and the internal mark
// ---------------------------------------------------------------------------

/** *To order · Ordered · Ready for pickup …* — §8.2's chip, 22 px with a 6 px dot. */
export function MaterialChip({ state }: { state: MaterialState }) {
  return (
    <StatusChip group="material" status={state} className="shrink-0 whitespace-nowrap">
      {MATERIALS.state[state]}
    </StatusChip>
  )
}

/** The dot beside a group's name and on the summary strip (§8.2's group colours). */
export const GROUP_DOT: Record<MaterialGroupKey, string> = {
  needs_ordering: 'bg-warn',
  on_the_way: 'bg-info',
  ready: 'bg-accent-dark',
  bought: 'bg-[rgb(var(--status-material-bought-fg))]',
  used: 'bg-slate-band-text',
}

/** The grey **Noted** tag (Board 18 B, C). */
export function NotedTag() {
  return (
    <span className="inline-flex h-[19px] shrink-0 items-center whitespace-nowrap rounded-[4px] bg-line-soft px-1.5
                     text-[11px] font-semibold text-text-muted">
      {COMMON.noted}
    </span>
  )
}

/** The amber *Swapped* tag after a swapped line's name (Board 2). */
export function SwappedTag() {
  return (
    <span className="inline-flex h-[19px] shrink-0 items-center gap-1 whitespace-nowrap rounded-[4px]
                     bg-amber-band-bg px-1.5 text-[11px] font-semibold text-amber-band-text">
      <VisitGlyph name="swap" size={11} stroke={2.2} />
      {MATERIALS.swapped}
    </span>
  )
}

/** *Needed*: a chip per day — *Day 2* teal when it is today's (Board 2). */
export function DayChips({ days }: { days: DayRef[] }) {
  return (
    <span className="inline-flex gap-[3px]">
      {days.map((dayRef) => (
        <span
          key={dayRef.index}
          className={cn(
            'inline-flex h-[19px] items-center whitespace-nowrap rounded-[5px] px-1.5 text-[10.5px] font-bold',
            dayRef.relative === 'today' ? 'bg-teal-band-bg text-teal-band-text' : 'bg-line-soft text-text-muted',
          )}
        >
          {COMMON.dayN(String(dayRef.index))}
        </span>
      ))}
    </span>
  )
}

/** The glyph each source wears (Board 2's *Comes from*). */
export const SOURCE_GLYPH: Record<MaterialSource, VisitGlyphName> = {
  yard: 'yard',
  order_site: 'site',
  order_yard: 'yard',
  order_pickup: 'pickup',
  field: 'receipt',
}

/** *Comes from*: the source with its glyph, the vendor (or the yard) under it. */
export function SourceCell({ line }: { line: MaterialLine }) {
  const where = line.vendor?.name ?? line.yard?.name ?? null
  return (
    <div className="flex min-w-0 flex-col">
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] font-semibold text-text">
        <VisitGlyph name={SOURCE_GLYPH[line.source]} size={14} stroke={2} />
        {MATERIALS.source[line.source]}
      </span>
      {where && <span className="ml-5 truncate text-[11.5px] text-text-muted">{where}</span>}
    </div>
  )
}

/** A PO number, green and clickable wherever drawn — it opens the PO. */
export function PoLink({ po }: { po: Pick<PoRef, 'id' | 'number'> }) {
  return (
    <Link
      to={poHref(po)}
      onClick={(event) => event.stopPropagation()}
      className="font-semibold text-accent hover:text-accent-dark"
    >
      {po.number}
    </Link>
  )
}

/** Where a PO opens — Purchasing's page for one order (`purchase-orders.tsx`, `?po=`). */
export const poHref = (po: { id: string }): string => `/purchasing/purchase-orders?po=${encodeURIComponent(po.id)}`

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** A signed difference in the tone it went (§3.2): amber over, green under, a faint *—* at zero. */
export function DeltaText({ delta, className }: { delta: Delta; className?: string }) {
  if (delta.tone === 'zero') return <span className={cn('text-text-faint', className)}>{'—'}</span>
  return (
    <span className={cn(delta.tone === 'over' ? 'text-warn' : 'text-accent-dark', className)}>
      {signedMoney(delta)}
    </span>
  )
}

export { money }

// ---------------------------------------------------------------------------
// Quantities — a number of things, never money
// ---------------------------------------------------------------------------

/** *420* · *1,200*. */
export const count = (value: number): string => formatNumber(value)

/** The unit for this many — *bag* or *bags*; `null` means *each* and prints nothing. */
export function unitWord(qty: number, unit: Unit | null): string {
  if (!unit) return ''
  return qty === 1 ? unit.one : unit.many
}

/** *9 bags* · *420 sq ft* · *24* (a line sold by the each prints the bare number). */
export function qtyText(qty: number, unit: Unit | null): string {
  const word = unitWord(qty, unit)
  return word ? `${count(qty)} ${word}` : count(qty)
}

// ---------------------------------------------------------------------------
// Buttons drawn at the boards' sizes
// ---------------------------------------------------------------------------

/** 28 high, 14 either side, 8 radius, 12/600 — a row's **Order** and **Swap** (Board 2). */
export function RowButton({
  primary = false, glyph, onClick, children, label, className,
}: {
  primary?: boolean
  glyph?: VisitGlyphName
  onClick: () => void
  children: ReactNode
  label?: string
  className?: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(event) => { event.stopPropagation(); onClick() }}
      className={cn(
        'inline-flex h-7 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-button border px-3.5',
        'text-[12px] font-semibold',
        primary
          ? 'border-accent bg-accent text-white hover:bg-accent-dark'
          : 'border-line bg-surface text-text hover:bg-hover',
        className,
      )}
    >
      {glyph && <VisitGlyph name={glyph} size={14} stroke={2} />}
      {children}
    </button>
  )
}

/** The 28 px ⋯ at a row's end — it hands its own button over, for the menu to hang from. */
export function MoreButton({ onClick, open, label }: {
  onClick: (anchor: HTMLElement) => void
  open: boolean
  label: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={(event) => { event.stopPropagation(); onClick(event.currentTarget) }}
      className={cn(
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-button',
        open ? 'bg-line-soft text-text' : 'text-text-muted hover:bg-hover',
      )}
    >
      <VisitGlyph name="more" size={15} stroke={2.4} />
    </button>
  )
}
